import type { Schema } from '@dealdesk/api-schema';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';

import { api, unwrap } from '@/shared/api/client';
import { FINANCE_QUERY } from '@/shared/api/stale';

import { collectionKeys, type LedgerScope } from './keys';

const scopeQuery = (s: LedgerScope) => ({ period: s.period, ...(s.bank ? { bank: s.bank } : {}) });

export const summaryQuery = (s: LedgerScope) =>
  queryOptions({
    queryKey: collectionKeys.summary(s),
    queryFn: ({ signal }) => unwrap(api.GET('/collections/summary', { params: { query: scopeQuery(s) }, signal })),
    ...FINANCE_QUERY,
  });

/** Chart series: the bank revenue position at the end of each of the last six months (finance rules: refetch on mount). */
export const trendQuery = (s: LedgerScope) =>
  queryOptions({
    queryKey: collectionKeys.trend(s),
    queryFn: ({ signal }) => unwrap(api.GET('/collections/trend', { params: { query: { ...scopeQuery(s), periods: 6 } }, signal })),
    ...FINANCE_QUERY,
  });

export interface ListParams extends LedgerScope {
  q?: string | undefined;
  sort?: string | undefined;
  page?: number | undefined;
  size?: number | undefined;
}

export const receivablesQuery = (p: ListParams & { type?: Schema<'EntryType'>[] }) =>
  queryOptions({
    queryKey: collectionKeys.receivables({ ...p }),
    queryFn: ({ signal }) => unwrap(api.GET('/receivables', { params: { query: { ...scopeQuery(p), ...(p.q ? { q: p.q } : {}), ...(p.sort ? { sort: p.sort } : {}), ...(p.page ? { page: p.page } : {}), ...(p.size ? { size: p.size } : {}), ...(p.type?.length ? { type: p.type } : {}) } }, signal })),
    ...FINANCE_QUERY,
    placeholderData: keepPreviousData,
  });

export const invoicesQuery = (p: ListParams) =>
  queryOptions({
    queryKey: collectionKeys.invoices({ ...p }),
    queryFn: ({ signal }) => unwrap(api.GET('/invoices', { params: { query: { ...scopeQuery(p), ...(p.q ? { q: p.q } : {}), ...(p.sort ? { sort: p.sort } : {}), ...(p.page ? { page: p.page } : {}), ...(p.size ? { size: p.size } : {}) } }, signal })),
    ...FINANCE_QUERY,
    placeholderData: keepPreviousData,
  });

export const invoiceQuery = (id: string) =>
  queryOptions({
    queryKey: collectionKeys.invoice(id),
    queryFn: ({ signal }) => unwrap(api.GET('/invoices/{invoiceId}', { params: { path: { invoiceId: id } }, signal })),
    ...FINANCE_QUERY,
  });

export const candidatesQuery = (bank: string, period: string) =>
  queryOptions({
    queryKey: collectionKeys.candidates(bank, period),
    queryFn: ({ signal }) => unwrap(api.GET('/invoices/candidates', { params: { query: { bank, period } }, signal })),
    ...FINANCE_QUERY,
  });

export const paymentsQuery = (p: ListParams) =>
  queryOptions({
    queryKey: collectionKeys.payments({ ...p }),
    queryFn: ({ signal }) => unwrap(api.GET('/payments', { params: { query: { ...scopeQuery(p), ...(p.q ? { q: p.q } : {}), ...(p.sort ? { sort: p.sort } : {}), ...(p.page ? { page: p.page } : {}), ...(p.size ? { size: p.size } : {}) } }, signal })),
    ...FINANCE_QUERY,
    placeholderData: keepPreviousData,
  });

export const paymentQuery = (id: string) =>
  queryOptions({
    queryKey: collectionKeys.payment(id),
    queryFn: ({ signal }) => unwrap(api.GET('/payments/{paymentId}', { params: { path: { paymentId: id } }, signal })),
    ...FINANCE_QUERY,
  });

export const runsQuery = (bank?: string) =>
  queryOptions({
    queryKey: collectionKeys.runs(bank),
    queryFn: ({ signal }) => unwrap(api.GET('/reconciliation/runs', { params: { query: bank ? { bank } : {} }, signal })),
    ...FINANCE_QUERY,
  });

export const runQuery = (id: string) =>
  queryOptions({
    queryKey: collectionKeys.run(id),
    queryFn: ({ signal }) => unwrap(api.GET('/reconciliation/runs/{runId}', { params: { path: { runId: id } }, signal })),
    ...FINANCE_QUERY,
  });

/** Closed revenue months (per bank). A month without a row is open. */
export const periodsQuery = (bank?: string) =>
  queryOptions({
    queryKey: collectionKeys.periods(bank),
    queryFn: ({ signal }) => unwrap(api.GET('/collections/periods', { params: { query: bank ? { bank } : {} }, signal })),
    ...FINANCE_QUERY,
  });

/** 5-minute link to the bank statement a reconciliation run was built from. */
export const fetchStatementLink = (runId: string, disposition: 'attachment' | 'inline') =>
  unwrap(api.GET('/reconciliation/runs/{runId}/statement/download', { params: { path: { runId }, query: { disposition } } }));
